import { Box, Button, Stack, Typography } from '@mui/material';
import { DiagnosesInput } from './DiagnosesInput';
import { DiagnosesGroupItem, DiagnosesItem, PropsDiagnosesContext } from './DiagnosesInput.components';
import { border, buildDataTestId, buildDataTestIdAttribute, log } from '@src/utils';
import { FormTextField } from '@components/FormFields';
import { useFormContext } from 'react-hook-form';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { diagnosesGroupsApiSlice, DiagnosesSearchValueType } from '@src/api';
import { defaultFilterValues } from './consts';
import { toast } from '@components/RootNotificationWrapper';
import { PreloaderBubbles } from '@components/PreloaderBubbles';

type DiagnosesGroupsTabProps = {
  handleClickScaleType: (value: DiagnosesSearchValueType) => void;
  dataTestId?: string;
};

export const DiagnosesGroupsTab: React.FC<DiagnosesGroupsTabProps> = ({ handleClickScaleType, dataTestId }) => {
  const { diagnosesInRule, setDiagnosesInRule, onDelete, scaleType, onDeleteDiagnosesGroups } =
    useContext(PropsDiagnosesContext);
  useEffect(() => {
    handleClickScaleType(DiagnosesSearchValueType.ICD10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { control, setValue } = useFormContext();

  const [nameDiagnosesGroup, setNameDiagnosesGroup] = useState<string>('');
  const [errorDublicateName, setErrorDublicateName] = useState('');

  const [createDiagnosesGroup, { isLoading }] = diagnosesGroupsApiSlice.useCreateDiagnosesGroupMutation();
  const { data: diagnosesGroups, isLoading: isLoadingGroups } =
    diagnosesGroupsApiSlice.useGetDiagnosesGroupsQuery(defaultFilterValues);

  useEffect(() => {
    return () => {
      setValue('DiagnosesGroup', '');
    };
  }, [setValue]);

  const onSaveDiagnosesGroup = async () => {
    const diagnosesIds = diagnosesInRule.map(elem => elem.id);
    if (nameDiagnosesGroup && diagnosesInRule.length > 0) {
      await createDiagnosesGroup({ name: nameDiagnosesGroup, diagnosesIds })
        .then((res: any) => {
          if (res.data.status === 200) {
            toast({
              variant: 'success',
              message: 'Diagnoses group was saved and is ready for use in the selections.',
              title: 'Success',
            });
            setDiagnosesInRule([]);
            setNameDiagnosesGroup('');
            setValue('DiagnosesGroup', '');
          }
          if (res.data.response.data.detail.includes('name already existed')) {
            setErrorDublicateName('Group name already exists');
          }
          return '';
        })
        .catch(log);
    }
  };

  const onChangeInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (errorDublicateName) {
      setErrorDublicateName('');
    }
    setNameDiagnosesGroup(e.target.value);
  };

  const onClearInput = () => {
    setNameDiagnosesGroup('');
    setErrorDublicateName('');
  };

  return (
    <Box height="calc(100% - 90px)" overflow="auto" mb="auto">
      <Box padding="24px">
        <Typography variant="body16Bold" color="grey.700" lineHeight="20px">
          Add new diagnosis group
        </Typography>
        <FormTextField
          control={control}
          onChange={onChangeInput}
          onClear={onClearInput}
          placeholder="Diagnosis group name"
          name={'DiagnosesGroup'}
          withErrorMessage
          label=""
          sx={{ marginBottom: '24px', marginTop: '24px' }}
          errorManual={errorDublicateName}
        />
        <DiagnosesInput heightPoper={359} scaleType={scaleType} isGroupsPage dataTestId={dataTestId} />
      </Box>

      <Stack>
        <Box padding="1px 24px" mb={diagnosesInRule.length > 0 ? '24px' : 0}>
          {diagnosesInRule.map((diagnosis, index) => {
            const backgroundColor = index % 2 === 0 ? '#F7FCFD' : '#FFFFFF';
            return (
              <DiagnosesItem
                sx={{
                  '&.Mui-expanded': {
                    border,
                    padding: 0,
                    margin: 0,
                  },
                  backgroundColor,
                }}
                level={0}
                withCheckbox={false}
                key={diagnosis.id}
                diagnosis={diagnosis}
                searchDiagnosesValue={''}
                onDelete={onDelete}
                backgroundColor={backgroundColor}
                dataTestId={buildDataTestId(dataTestId, `diagnoses-in-rule-group-${index}`)}
              />
            );
          })}
        </Box>
        <Box margin="0 24px 24px auto">
          <Button
            color="secondary"
            variant="contained"
            onClick={onSaveDiagnosesGroup}
            disabled={diagnosesInRule.length === 0 || isLoading === true || nameDiagnosesGroup === ''}
            {...buildDataTestIdAttribute(dataTestId, 'button-save')}
          >
            Save
          </Button>
        </Box>
        {diagnosesInRule.length === 0 ? (
          <Stack gap="24px" mt="6px">
            <Box padding="1px 24px">
              <Typography variant="body16Bold" color="grey.700" lineHeight="20px">
                Diagnoses groups
              </Typography>
            </Box>

            <Stack padding="1px 24px">
              <Stack>
                {isLoadingGroups && <PreloaderBubbles overlay="light" size={20} />}
                {diagnosesGroups
                  ? diagnosesGroups.map((group, index) => {
                      const backgroundColor = index % 2 === 0 ? '#F7FCFD' : '#FFFFFF';
                      return (
                        <DiagnosesGroupItem
                          id={group.id}
                          name={group.name}
                          sx={{ backgroundColor }}
                          backgroundColor={backgroundColor}
                          onDeleteDiagnosesGroupsFromRule={onDeleteDiagnosesGroups}
                          withDeleteItem
                          dataTestId={buildDataTestId(dataTestId, `diagnoses-group-${index}`)}
                        />
                      );
                    })
                  : null}
              </Stack>
            </Stack>
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
};
